/**
 * Advanced Notification Manager
 * Handles in-app notifications, push notifications, and notification queue management
 */

// ID generation removed - notifications are local UI state, using timestamp-based IDs

export interface NotificationConfig {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  duration?: number; // milliseconds, 0 for persistent
  actions?: NotificationAction[];
  data?: Record<string, unknown>;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  category?: string;
  persistent?: boolean;
  showProgress?: boolean;
  progress?: number; // 0-100
  timestamp?: Date;
  userId?: string;
  workflowId?: string;
  taskId?: string;
}

export interface NotificationAction {
  id: string;
  label: string;
  action: (notification: NotificationConfig) => void | Promise<void>;
  primary?: boolean;
  destructive?: boolean;
}

export interface NotificationFilter {
  types?: NotificationConfig['type'][];
  priorities?: NotificationConfig['priority'][];
  categories?: string[];
  userIds?: string[];
  workflowIds?: string[];
  timeRange?: {
    start: Date;
    end: Date;
  };
}

export interface NotificationSubscription {
  id: string;
  filter: NotificationFilter;
  callback: (notification: NotificationConfig) => void;
}

export interface PushNotificationConfig {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  image?: string;
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
  data?: Record<string, unknown>;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
}

export class NotificationManager {
  private notifications: Map<string, NotificationConfig> = new Map();
  private subscriptions: Map<string, NotificationSubscription> = new Map();
  private queue: NotificationConfig[] = [];
  private isProcessing = false;
  private maxNotifications = 100;
  private defaultDuration = 5000;
  private pushSubscription: PushSubscription | null = null;
  private vapidPublicKey: string | null = null;

  constructor(options: {
    maxNotifications?: number;
    defaultDuration?: number;
    vapidPublicKey?: string;
  } = {}) {
    this.maxNotifications = options.maxNotifications || 100;
    this.defaultDuration = options.defaultDuration || 5000;
    this.vapidPublicKey = options.vapidPublicKey || null;

    this.initializePushNotifications();
    this.startQueueProcessor();
  }

  // Core notification methods

  public show(config: Omit<NotificationConfig, 'id' | 'timestamp'>): string {
    const notification: NotificationConfig = {
      id: this.generateId(),
      timestamp: new Date(),
      duration: this.defaultDuration,
      ...config
    };

    this.addToQueue(notification);
    return notification.id;
  }

  public update(id: string, updates: Partial<NotificationConfig>): boolean {
    const existing = this.notifications.get(id);
    if (!existing) return false;

    const updated = { ...existing, ...updates };
    this.notifications.set(id, updated);
    this.notifySubscribers(updated);
    return true;
  }

  public dismiss(id: string): boolean {
    const notification = this.notifications.get(id);
    if (!notification) return false;

    this.notifications.delete(id);
    this.notifySubscribers({ ...notification, type: 'info' } as NotificationConfig);
    return true;
  }

  public dismissAll(filter?: NotificationFilter): number {
    const toRemove = this.getFiltered(filter);
    toRemove.forEach(notification => this.dismiss(notification.id));
    return toRemove.length;
  }

  public get(id: string): NotificationConfig | undefined {
    return this.notifications.get(id);
  }

  public getAll(filter?: NotificationFilter): NotificationConfig[] {
    return this.getFiltered(filter);
  }

  public getCount(filter?: NotificationFilter): number {
    return this.getFiltered(filter).length;
  }

  // Convenience methods for different notification types

  public info(title: string, message: string, options?: Partial<NotificationConfig>): string {
    return this.show({
      title,
      message,
      type: 'info',
      priority: 'normal',
      ...options
    });
  }

  public success(title: string, message: string, options?: Partial<NotificationConfig>): string {
    return this.show({
      title,
      message,
      type: 'success',
      priority: 'normal',
      ...options
    });
  }

  public warning(title: string, message: string, options?: Partial<NotificationConfig>): string {
    return this.show({
      title,
      message,
      type: 'warning',
      priority: 'high',
      duration: 8000,
      ...options
    });
  }

  public error(title: string, message: string, options?: Partial<NotificationConfig>): string {
    return this.show({
      title,
      message,
      type: 'error',
      priority: 'urgent',
      duration: 0, // Persistent
      persistent: true,
      ...options
    });
  }

  // Progress notifications

  public showProgress(
    title: string,
    message: string,
    progress: number = 0,
    options?: Partial<NotificationConfig>
  ): string {
    return this.show({
      title,
      message,
      type: 'info',
      priority: 'normal',
      duration: 0,
      showProgress: true,
      progress,
      persistent: true,
      ...options
    });
  }

  public updateProgress(id: string, progress: number, message?: string): boolean {
    const updates: Partial<NotificationConfig> = { progress };
    if (message) updates.message = message;
    
    return this.update(id, updates);
  }

  public completeProgress(id: string, successMessage?: string): boolean {
    return this.update(id, {
      progress: 100,
      type: 'success',
      message: successMessage || 'Completed successfully',
      duration: 3000,
      persistent: false,
      showProgress: false
    });
  }

  // Workflow-specific notifications

  public workflowStarted(workflowId: string, workflowName: string): string {
    return this.info(
      'Workflow Started',
      `${workflowName} is now running`,
      {
        category: 'workflow',
        workflowId,
        actions: [
          {
            id: 'view',
            label: 'View Details',
            action: (notification) => {
              // Navigate to workflow details
              window.location.href = `/workflows/${workflowId}`;
            }
          }
        ]
      }
    );
  }

  public workflowCompleted(workflowId: string, workflowName: string, success: boolean): string {
    return success
      ? this.success(
          'Workflow Completed',
          `${workflowName} finished successfully`,
          { category: 'workflow', workflowId }
        )
      : this.error(
          'Workflow Failed',
          `${workflowName} encountered an error`,
          { category: 'workflow', workflowId }
        );
  }

  public taskUpdate(taskId: string, taskName: string, status: string): string {
    return this.info(
      'Task Update',
      `${taskName} is now ${status}`,
      {
        category: 'task',
        taskId,
        duration: 3000
      }
    );
  }

  // Subscription management

  public subscribe(
    filter: NotificationFilter,
    callback: (notification: NotificationConfig) => void
  ): string {
    const id = this.generateId();
    const subscription: NotificationSubscription = {
      id,
      filter,
      callback
    };
    
    this.subscriptions.set(id, subscription);
    return id;
  }

  public unsubscribe(subscriptionId: string): boolean {
    return this.subscriptions.delete(subscriptionId);
  }

  // Push notification methods

  private async initializePushNotifications(): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !this.vapidPublicKey) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        this.pushSubscription = subscription;
      }
    } catch (error) {
      console.warn('Push notification initialization failed:', error);
    }
  }

  public async requestPushPermission(): Promise<boolean> {
    if (!('Notification' in window)) return false;

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  public async subscribeToPush(): Promise<PushSubscription | null> {
    if (!this.vapidPublicKey || !('serviceWorker' in navigator)) return null;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
      });

      this.pushSubscription = subscription;
      return subscription;
    } catch (error) {
      console.error('Push subscription failed:', error);
      return null;
    }
  }

  public async sendPushNotification(config: PushNotificationConfig): Promise<boolean> {
    if (!this.pushSubscription) return false;

    try {
      // This would typically send the notification via your server
      // For demo purposes, we'll show a browser notification
      if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification(config.title, {
          body: config.body,
          icon: config.icon,
          badge: config.badge,
          // image: config.image, // TODO: image property not supported in this NotificationOptions interface version
          tag: config.tag,
          requireInteraction: config.requireInteraction,
          silent: config.silent,
          data: config.data,
          // actions: config.actions, // TODO: actions property not supported in this NotificationOptions interface version
        });

        return true;
      }
    } catch (error) {
      console.error('Push notification failed:', error);
    }

    return false;
  }

  // Queue management

  private addToQueue(notification: NotificationConfig): void {
    // Check priority and insert accordingly
    const insertIndex = this.findInsertIndex(notification);
    this.queue.splice(insertIndex, 0, notification);

    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  private findInsertIndex(notification: NotificationConfig): number {
    const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
    const notificationPriority = priorityOrder[notification.priority];

    for (let i = 0; i < this.queue.length; i++) {
      const queuePriority = priorityOrder[this.queue[i]?.priority ?? 'normal'];
      if (notificationPriority < queuePriority) {
        return i;
      }
    }

    return this.queue.length;
  }

  private startQueueProcessor(): void {
    setInterval(() => {
      if (!this.isProcessing && this.queue.length > 0) {
        this.processQueue();
      }
    }, 100);
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;

    while (this.queue.length > 0) {
      const notification = this.queue.shift()!;
      await this.processNotification(notification);
      
      // Small delay between notifications
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    this.isProcessing = false;
  }

  private async processNotification(notification: NotificationConfig): Promise<void> {
    // Add to active notifications
    this.notifications.set(notification.id, notification);

    // Enforce maximum notifications limit
    if (this.notifications.size > this.maxNotifications) {
      const oldest = Array.from(this.notifications.values())
        .sort((a, b) => a.timestamp!.getTime() - b.timestamp!.getTime())[0];
      if (oldest) {
        this.dismiss(oldest.id);
      }
    }

    // Notify subscribers
    this.notifySubscribers(notification);

    // Auto-dismiss if duration is set
    if (notification.duration && notification.duration > 0 && !notification.persistent) {
      setTimeout(() => {
        this.dismiss(notification.id);
      }, notification.duration);
    }
  }

  private notifySubscribers(notification: NotificationConfig): void {
    this.subscriptions.forEach(subscription => {
      if (this.matchesFilter(notification, subscription.filter)) {
        try {
          subscription.callback(notification);
        } catch (error) {
          console.error('Notification subscriber error:', error);
        }
      }
    });
  }

  private matchesFilter(notification: NotificationConfig, filter: NotificationFilter): boolean {
    if (filter.types && !filter.types.includes(notification.type)) return false;
    if (filter.priorities && !filter.priorities.includes(notification.priority)) return false;
    if (filter.categories && notification.category && !filter.categories.includes(notification.category)) return false;
    if (filter.userIds && notification.userId && !filter.userIds.includes(notification.userId)) return false;
    if (filter.workflowIds && notification.workflowId && !filter.workflowIds.includes(notification.workflowId)) return false;
    
    if (filter.timeRange && notification.timestamp) {
      if (notification.timestamp < filter.timeRange.start || notification.timestamp > filter.timeRange.end) {
        return false;
      }
    }

    return true;
  }

  private getFiltered(filter?: NotificationFilter): NotificationConfig[] {
    const all = Array.from(this.notifications.values());
    if (!filter) return all;

    return all.filter(notification => this.matchesFilter(notification, filter));
  }

  // Utility methods

  private generateId(): string {
    // Generate client-side ID for local UI state (notifications aren't persisted)
    return `notif_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private urlBase64ToUint8Array(base64String: string): BufferSource {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Analytics and monitoring

  public getStats(): {
    total: number;
    byType: Record<string, number>;
    byPriority: Record<string, number>;
    byCategory: Record<string, number>;
    queueLength: number;
  } {
    const notifications = Array.from(this.notifications.values());
    
    const byType: Record<string, number> = {};
    const byPriority: Record<string, number> = {};
    const byCategory: Record<string, number> = {};

    notifications.forEach(notification => {
      byType[notification.type] = (byType[notification.type] || 0) + 1;
      byPriority[notification.priority] = (byPriority[notification.priority] || 0) + 1;
      if (notification.category) {
        byCategory[notification.category] = (byCategory[notification.category] || 0) + 1;
      }
    });

    return {
      total: notifications.length,
      byType,
      byPriority,
      byCategory,
      queueLength: this.queue.length
    };
  }

  public export(): NotificationConfig[] {
    return Array.from(this.notifications.values());
  }

  public import(notifications: NotificationConfig[]): void {
    notifications.forEach(notification => {
      this.notifications.set(notification.id, notification);
    });
  }

  public clear(): void {
    this.notifications.clear();
    this.queue.length = 0;
  }

  public destroy(): void {
    this.clear();
    this.subscriptions.clear();
  }
}